import { compact } from 'lodash';
import { RelatedDataStore } from './related-data-store';
import { NewEventFields, RelatedDataType } from './types';
import { RelatedDataMatchingCriteria } from '@mark43/cad-api';
import { startCase, trimEnd } from 'lodash';

export const buildFromFieldsForRequestData = (
    cadEventsFrom?: number,
    rmsReportsFrom?: number,
    vehiclesFrom?: number
) => {
    if (cadEventsFrom === undefined && rmsReportsFrom === undefined && vehiclesFrom === undefined) {
        return undefined;
    } else {
        return {
            ...(cadEventsFrom !== undefined ? { cadEventsFrom } : {}),
            ...(rmsReportsFrom !== undefined ? { rmsReportsFrom } : {}),
            ...(vehiclesFrom !== undefined ? { vehiclesFrom } : {}),
        };
    }
};

export const mapNewEventFieldNamesForRequest = ({
    phoneNumbers,
    vehicleQueries,
    relatedDataLocationQuery: cadNextRelatedDataLocationQuery,
    personQueries,
}: NewEventFields) => {
    return { phoneNumbers, vehicleQueries, cadNextRelatedDataLocationQuery, personQueries };
};

export const determineTypesToUpdateNextFrom = (
    typesToUpdate: RelatedDataType[],
    isViewMoreRequest: boolean,
    store: RelatedDataStore
) => {
    // If it's a view more request, update 'nextFrom' for all types provided.
    if (isViewMoreRequest) {
        return [...typesToUpdate];
    }

    // For poll requests, update 'nextFrom' for only types which have polling enabled.
    return compact([
        store.isCadEventsPollEnabled ? 'CAD_EVENTS' : undefined,
        store.isRmsReportsPollEnabled ? 'RMS_REPORTS' : undefined,
        store.isVehiclesPollEnabled ? 'VEHICLES' : undefined,
    ]);
};

export const determineTypesToReplaceOrUpsert = (
    typesToUpdate: RelatedDataType[],
    isViewMoreRequest: boolean,
    store: RelatedDataStore,
    enhancedRelatedDataEnabled: boolean
) => {
    // If feature flag is off, always replace entities, don't upsert them
    if (!enhancedRelatedDataEnabled) {
        return { typesToReplace: [...typesToUpdate], typesToUpsert: [] };
    }

    // If it's a view more request, always upsert the entities, don't replace them.
    if (isViewMoreRequest) {
        return { typesToReplace: [], typesToUpsert: [...typesToUpdate] };
    }

    // If it's a poll request, replace entities which have polling enabled, don't upsert them
    return {
        typesToReplace: compact([
            store.isCadEventsPollEnabled ? 'CAD_EVENTS' : undefined,
            store.isRmsReportsPollEnabled ? 'RMS_REPORTS' : undefined,
            store.isVehiclesPollEnabled ? 'VEHICLES' : undefined,
        ]),
        typesToUpsert: [],
    };
};

export const buildMatchingCriteriaString = (criteria?: RelatedDataMatchingCriteria): string => {
    if (criteria === undefined) {
        return '';
    }
    const names = Object.keys(criteria)
        .filter((key) => {
            const value = criteria[key as keyof RelatedDataMatchingCriteria];
            return (
                value !== undefined && value !== '' && !(Array.isArray(value) && value.length === 0)
            );
        })
        .map((key) => trimEnd(startCase(key), 's'));
    return names.length === 0 ? '' : names.join(', ');
};
